// File: frontend/src/context/BrandingContext.js
// Version: 1.1 (Phase 9 — features из /branding, аддитивно)
// Description: Single source of truth for tenant identity on the frontend.
// Replaces 3 duplicate fetch('/api/v1/branding') calls (Login.js:19, Home.js:37, Navbar.js:89).
// Owns the html.fitenko class toggle and conditional Fontsource CSS loading.

import React, { createContext, useContext, useEffect, useState } from 'react';

// 🔴 Нейтральное начальное состояние (instance_name: null). До успешного ответа /branding
//    не активируем ни один арендатор и не вешаем специфичные классы.
const DEFAULT_BRANDING = Object.freeze({ instance_name: null, company_name: '' });

// Повторы при сбое: быстрые паузы перекрывают рестарт бэкенда при выкатке (~10–30 с).
// 🔴 После них — НЕ сдаёмся, а повторяем раз в BRANDING_RETRY_FOREVER_MS. 17.10.2026 бэкенд
//    лежал дольше 30 с, повторы кончились, и интерфейс остался в старой оболочке «MLG OS».
//    Пока ответа нет, приложение показывает экран подключения (App.js), а не чужую оболочку.
export const BRANDING_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
export const BRANDING_RETRY_FOREVER_MS = 15000;

// Ответ годен, только если это объект с непустым instance_name: тело 502/500 (HTML или {detail})
// не должно подменить арендатора.
const godenOtvet = (data) => !!data && typeof data === 'object' && typeof data.instance_name === 'string' && data.instance_name.trim() !== '';

// Phase 9: признаки арендатора из /api/v1/branding (`features`). Нет поля / не объект → ПУСТО
// (у Fitenko/MLG до и после выкатки 09-02 — `{}`). Замороженный синглтон: ссылка стабильна
// для зависимостей эффектов.
const PUSTO_PRIZNAKOV = Object.freeze({});

const BrandingContext = createContext({
    branding: DEFAULT_BRANDING,
    isFitenko: false,
    loading: true,
    features: PUSTO_PRIZNAKOV,
    featuresError: null,
});

export const useBranding = () => useContext(BrandingContext);

// Module-scoped cache mirrors Navbar.js:11 `_brandingCache` pattern.
let _cache = null;

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(_cache || DEFAULT_BRANDING);
    const [loading, setLoading] = useState(!_cache);
    // true — быстрые повторы исчерпаны, сервер всё ещё не ответил (повторы продолжаются).
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (_cache) return undefined;
        let otmeneno = false;
        let tajmer = null;
        const popytka = (nomer) => {
            fetch('/api/v1/branding')
                .then((r) => {
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    return r.json();
                })
                .then((data) => {
                    if (!godenOtvet(data)) throw new Error('branding: нет instance_name');
                    if (otmeneno) return;
                    _cache = data;
                    setBranding(data);
                    setLoading(false);
                    setFailed(false);
                    try {
                        localStorage.setItem('lastTenant', data.instance_name);
                        if (data.instance_name === 'fitenko' || data?.features?.shell === 'rail') {
                            localStorage.setItem('lastShell', 'rail');
                        } else {
                            localStorage.removeItem('lastShell');
                        }
                    } catch (e) { /* localStorage disabled */ }
                })
                .catch(() => {
                    if (otmeneno) return;
                    if (nomer < BRANDING_RETRY_DELAYS_MS.length) {
                        tajmer = setTimeout(() => popytka(nomer + 1), BRANDING_RETRY_DELAYS_MS[nomer]);
                    } else {
                        setFailed(true); // loading остаётся true: арендатор не известен
                        tajmer = setTimeout(() => popytka(nomer + 1), BRANDING_RETRY_FOREVER_MS);
                    }
                });
        };
        popytka(0);
        return () => { otmeneno = true; if (tajmer) clearTimeout(tajmer); };
    }, []);

    const isFitenko = branding.instance_name === 'fitenko';
    const features = (branding && branding.features && typeof branding.features === 'object' && !Array.isArray(branding.features)) ? branding.features : PUSTO_PRIZNAKOV;
    const featuresError = (branding && branding.features_error) || null;
    const isRail = isFitenko || features?.shell === 'rail';

    // Idempotent class toggle (inline <script> in index.html may have already set it).
    useEffect(() => {
        const html = document.documentElement;
        if (isFitenko) html.classList.add('fitenko');
        else html.classList.remove('fitenko');

        if (isRail) html.classList.add('shell-rail');
        else html.classList.remove('shell-rail');

        if (branding?.accent_color) {
            html.style.setProperty('--gold', branding.accent_color);
            html.style.setProperty('--primary-gold', branding.accent_color);
        } else {
            html.style.removeProperty('--gold');
            html.style.removeProperty('--primary-gold');
        }
    }, [isFitenko, isRail, branding?.accent_color]);

    // Conditional font CSS — Webpack code-splits these into their own chunk.
    // Гарнитуры §3.1 макета: Manrope (интерфейс) и DM Serif Display (заголовки).
    // MLG bundle truly does not contain these .woff2 — импорт живёт под рубежом.
    useEffect(() => {
        if (!isRail) return;
        Promise.all([
            import('@fontsource/manrope/400.css'),
            import('@fontsource/manrope/500.css'),
            import('@fontsource/manrope/600.css'),
            import('@fontsource/manrope/700.css'),
            import('@fontsource/dm-serif-display/400.css'),
        ]).catch(() => { /* font load failure non-fatal — system fallback used */ });
    }, [isRail]);

    return (
        <BrandingContext.Provider value={{ branding, isFitenko, loading, failed, features, featuresError }}>
            {children}
        </BrandingContext.Provider>
    );
};
